"use client";

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { useSession } from "next-auth/react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useAssetsContext, AssetFile } from "@/contexts/AssetsContext";

interface AssetsPanelProps {
  onUseAsset: (fileId: string, signedUrl: string) => void;
  onCloseSidebar?: () => void;
  checkReplaceNeeded?: () => Promise<{ needsConfirmation: boolean; hasImage: boolean; hasGeneratedResults: boolean }>;
  getCurrentImageFileId?: () => string | null;
}

type FilterTab = 'all' | 'uploads' | 'saved';

export default function AssetsPanel({ onUseAsset, onCloseSidebar, checkReplaceNeeded, getCurrentImageFileId }: AssetsPanelProps) {
  const { data: session } = useSession();
  const { assets, isLoading: assetsLoading, refreshAssets } = useAssetsContext();
  const [files, setFiles] = useState<AssetFile[]>([]); // Filtered files for sidebar
  const [allFiles, setAllFiles] = useState<AssetFile[]>([]); // Filtered files for modal
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [modalActiveTab, setModalActiveTab] = useState<FilterTab>('all');

  const [fileToDelete, setFileToDelete] = useState<AssetFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fileToReplace, setFileToReplace] = useState<AssetFile | null>(null);
  const [showAllAssetsModal, setShowAllAssetsModal] = useState(false);
  const [openMenuFileId, setOpenMenuFileId] = useState<string | null>(null);
  const menuButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const [enlargedImage, setEnlargedImage] = useState<AssetFile | null>(null);
  const [enlargedImageIndex, setEnlargedImageIndex] = useState<number>(-1);
  const [imageLoading, setImageLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Filter files by tab type
  // Note: API already filters to show only uploads + saved generated images
  // This is just for client-side tab filtering
  const filterFilesByTab = (filesToFilter: AssetFile[], tab: FilterTab): AssetFile[] => {
    if (tab === 'all') return filesToFilter;
    if (tab === 'uploads') return filesToFilter.filter(f => f.fileType === 'upload');
    if (tab === 'saved') return filesToFilter.filter(f => f.fileType === 'generated' && f.saved);
    return filesToFilter;
  };

  // Handle mounting for portal
  useEffect(() => {
    setMounted(true);
  }, []);


  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuFileId && !(event.target as HTMLElement).closest('[data-asset-menu]')) {
        setOpenMenuFileId(null);
        setMenuPosition(null);
      }
    };

    if (openMenuFileId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuFileId]);

  const getCurrentImageList = () => {
    return allFiles.length > 0 ? allFiles : files;
  };

  // Keyboard navigation for enlarged image
  useEffect(() => {
    if (!enlargedImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const currentList = getCurrentImageList();
      if (e.key === 'ArrowLeft' && enlargedImageIndex > 0) {
        const newIndex = enlargedImageIndex - 1;
        setEnlargedImage(currentList[newIndex]);
        setEnlargedImageIndex(newIndex);
      } else if (e.key === 'ArrowRight' && enlargedImageIndex < currentList.length - 1) {
        const newIndex = enlargedImageIndex + 1;
        setEnlargedImage(currentList[newIndex]);
        setEnlargedImageIndex(newIndex);
      } else if (e.key === 'Escape') {
        setEnlargedImage(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enlargedImage, enlargedImageIndex, allFiles, files]);

  const handleEnlargeImage = (file: AssetFile) => {
    const currentList = getCurrentImageList();
    const index = currentList.findIndex(f => f.fileId === file.fileId);
    setEnlargedImage(file);
    setEnlargedImageIndex(index);
    setImageLoading(true);
  };

  const handlePreviousImage = () => {
    const currentList = getCurrentImageList();
    if (currentList.length === 0 || enlargedImageIndex <= 0) return;

    const newIndex = enlargedImageIndex - 1;
    setEnlargedImage(currentList[newIndex]);
    setEnlargedImageIndex(newIndex);
    setImageLoading(true);
  };

  const handleNextImage = () => {
    const currentList = getCurrentImageList();
    if (currentList.length === 0 || enlargedImageIndex >= currentList.length - 1) return;

    const newIndex = enlargedImageIndex + 1;
    setEnlargedImage(currentList[newIndex]);
    setEnlargedImageIndex(newIndex);
    setImageLoading(true);
  };

  // Use assets from global context instead of fetching
  useEffect(() => {
    setIsLoading(assetsLoading);
  }, [assetsLoading]);

  // Update sidebar files when tab changes or assets update (client-side filtering)
  useEffect(() => {
    if (assets.length > 0) {
      setFiles(filterFilesByTab(assets, activeTab).slice(0, 50));
    } else {
      setFiles([]);
    }
  }, [activeTab, assets]);

  // Update modal files when tab changes or assets update (client-side filtering)
  useEffect(() => {
    if (showAllAssetsModal && assets.length > 0) {
      setAllFiles(filterFilesByTab(assets, modalActiveTab));
    } else {
      setAllFiles([]);
    }
  }, [modalActiveTab, assets, showAllAssetsModal]);

  const handleUseFile = async (file: AssetFile) => {
    // Check if replacement confirmation is needed
    if (checkReplaceNeeded) {
      const checkResult = await checkReplaceNeeded();
      if (checkResult.needsConfirmation && checkResult.hasImage && checkResult.hasGeneratedResults) {
        // Show replace confirmation modal
        setFileToReplace(file);
        return;
      }
    }

    // No confirmation needed, proceed (even if it's the same image)
    onUseAsset(file.fileId, file.signedUrl);
  };

  const handleReplaceConfirm = () => {
    if (fileToReplace) {
      onUseAsset(fileToReplace.fileId, fileToReplace.signedUrl);
      setFileToReplace(null);
    }
  };

  const handleReplaceCancel = () => {
    setFileToReplace(null);
  };

  const handleDeleteClick = (file: AssetFile, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setFileToDelete(file);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete || isDeleting) return;

    setIsDeleting(true);

    try {
      const response = await fetch(`/api/storage/file/${fileToDelete.fileId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Refresh assets from server to get updated list
        await refreshAssets();

        // Close modal only after successful deletion
        setFileToDelete(null);
      } else {
        console.error('Delete failed:', response.statusText);
        // Keep modal open on error so user can try again
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
      // Keep modal open on error so user can try again
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (isDeleting) return; // Prevent canceling during deletion
    setFileToDelete(null);
    setIsDeleting(false);
  };

  const handleViewAllAssets = () => {
    // Close the sidebar when opening the modal
    if (onCloseSidebar) {
      onCloseSidebar();
    }
    setShowAllAssetsModal(true);
    setModalActiveTab(activeTab); // Sync with current tab
    // Files will be filtered automatically via useEffect
  };

  const handleModalTabChange = (tab: FilterTab) => {
    setModalActiveTab(tab);
    // Files will be filtered automatically via useEffect
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString();
  };

  const truncateFileName = (fileName: string, maxLength: number = 20): string => {
    if (fileName.length <= maxLength) return fileName;

    // Extract extension
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // No extension, just truncate with ellipsis
      return fileName.substring(0, 12) + '..' + fileName.substring(fileName.length - 4);
    }

    const nameWithoutExt = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    if (nameWithoutExt.length <= 20) {
      return fileName; // Name is short enough (12 + 4 = 16)
    }

    // Format as "XXXXXXXXXXXX..XXXX.EXT" (12 chars before, 4 chars after)
    const start = nameWithoutExt.substring(0, 20);
    const end = nameWithoutExt.substring(nameWithoutExt.length - 4);
    return `${start}..${end}${extension}`;
  };

  if (!session?.user) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <p className="text-sm text-gray-600">Sign in to access your files</p>
      </div>
    );
  }

  return (
    <>
      {/* Filter Tabs and View All Link */}
      <div className="flex items-center justify-between mb-4 border-b border-gray-200">
        <div className="flex gap-2">
          {(['all', 'uploads', 'saved'] as FilterTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'all' ? 'All' : tab === 'uploads' ? 'Uploads' : 'Saved'}
            </button>
          ))}
        </div>
        <button
          onClick={handleViewAllAssets}
          className="hidden md:block text-xs text-primary hover:text-primary/80 font-medium transition-colors"
        >
          View All →
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && files.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-sm text-gray-600 mb-2">No images yet</p>
          <p className="text-xs text-gray-400">Upload an image or generate with a tool</p>
        </div>
      )}

      {/* Files Grid */}
      {!isLoading && files.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {files.map((file) => (
            <div
              key={file.fileId}
              className="relative group"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('assetFileId', file.fileId);
                e.dataTransfer.setData('assetSignedUrl', file.signedUrl);
                e.dataTransfer.effectAllowed = 'copy';
              }}
            >
              {/* Thumbnail */}
              <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                <Image
                  src={file.thumbnailUrl}
                  alt={file.originalName}
                  fill
                  className="object-cover"
                  unoptimized // Signed URLs already optimized
                />

                {/* 3-dots Menu Button - Top Right */}
                <button
                  ref={(el) => {
                    if (el) {
                      menuButtonRefs.current.set(file.fileId, el);
                    } else {
                      menuButtonRefs.current.delete(file.fileId);
                    }
                  }}
                  data-asset-menu
                  onClick={(e) => {
                    e.stopPropagation();
                    const button = e.currentTarget;
                    if (openMenuFileId === file.fileId) {
                      setOpenMenuFileId(null);
                      setMenuPosition(null);
                    } else {
                      if (typeof window !== 'undefined') {
                        const rect = button.getBoundingClientRect();
                        setMenuPosition({
                          top: rect.bottom + 4,
                          right: window.innerWidth - rect.right,
                        });
                      }
                      setOpenMenuFileId(file.fileId);
                    }
                  }}
                  className="absolute top-2 right-2 z-20 p-2 bg-black/60 backdrop-blur-sm border-0 border-white/80 text-white rounded-lg hover:bg-black/80 transition-colors shadow-lg"
                  title="More options"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
              </div>

              {/* File Info */}
              <div className="mt-1 px-1">
                <p className="text-xs text-gray-600 truncate">{formatFileSize(file.sizeBytes)}</p>
                <p className="text-xs text-gray-400">{formatDate(file.createdAt)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Menu Popover - Rendered via Portal (not constrained by image container) */}
      {mounted && openMenuFileId && menuPosition && menuButtonRefs.current.has(openMenuFileId) && (
        (() => {
          const file = files.find(f => f.fileId === openMenuFileId) || allFiles.find(f => f.fileId === openMenuFileId);
          if (!file) return null;
          const isInModal = allFiles.find(f => f.fileId === openMenuFileId) !== undefined;

          return createPortal(
            <div
              data-asset-menu
              className="fixed z-[10016] bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[160px]"
              style={{
                top: `${menuPosition.top}px`,
                right: `${menuPosition.right}px`,
              }}
            >
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isInModal) {
                  onUseAsset(file.fileId, file.signedUrl);
                  setShowAllAssetsModal(false);
                } else {
                  handleUseFile(file);
                }
                setOpenMenuFileId(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Use in Editor
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEnlargeImage(file);
                setOpenMenuFileId(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
              View
            </button>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                try {
                  const response = await fetch(file.signedUrl);
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = file.originalName;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  console.error('Download failed:', error);
                }
                setOpenMenuFileId(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleDeleteClick(file, e);
                setOpenMenuFileId(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>,
            document.body
          );
        })()
      )}

      {/* Replace Image Confirmation Modal */}
      {fileToReplace && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10025] p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="replace-modal-title">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col">
              {/* Icon and Content */}
              <div className="p-6 flex flex-col">
                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 id="replace-modal-title" className="text-lg font-medium text-gray-900 mb-1">Replace Current Image?</h3>
                    <p className="text-sm text-gray-600">
                      You have an image loaded with generated results. Replacing it will clear all generated results and start fresh. Are you sure you want to continue?
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleReplaceConfirm}
                    className="w-full px-4 py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Replace Image
                  </button>
                  <button
                    onClick={handleReplaceCancel}
                    className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {fileToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10025] p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col">
              {/* Image */}
              <div className="relative w-full h-48 bg-gray-100">
                <Image
                  src={fileToDelete.thumbnailUrl}
                  alt={fileToDelete.originalName}
                  fill
                  className="object-cover"
                  unoptimized
                />
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col">
                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 id="delete-modal-title" className="text-lg font-medium text-gray-900 mb-1">Delete Image?</h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to delete <span className="font-medium">"{truncateFileName(fileToDelete.originalName)}"</span>?
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={isDeleting}
                    className="w-full px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isDeleting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </button>
                  <button
                    onClick={handleDeleteCancel}
                    disabled={isDeleting}
                    className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View All Assets Modal - Rendered via Portal */}
      {mounted && showAllAssetsModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10015] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 pb-0  ">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">All Assets</h2>
                  <p className="text-sm text-gray-500 mt-1">{allFiles.length} total files</p>
                </div>
                <button
                  onClick={() => setShowAllAssetsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-2 border-b border-gray-200">
                {(['all', 'uploads', 'saved'] as FilterTab[]).map(tab => (
                  <button
                    key={tab}
                    onClick={() => handleModalTabChange(tab)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      modalActiveTab === tab
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab === 'all' ? 'All' : tab === 'uploads' ? 'Uploads' : 'Saved'}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingAll ? (
                <div className="flex items-center justify-center h-full">
                  <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : allFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-gray-600">No assets found</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {allFiles.map((file) => (
                    <div
                      key={file.fileId}
                      className="relative group cursor-pointer"
                    >
                      {/* Thumbnail */}
                      <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        <Image
                          src={file.thumbnailUrl}
                          alt={file.originalName}
                          fill
                          className="object-cover"
                          unoptimized
                        />

                        {/* 3-dots Menu Button - Top Right */}
                        <button
                          ref={(el) => {
                            if (el) {
                              menuButtonRefs.current.set(file.fileId, el);
                            } else {
                              menuButtonRefs.current.delete(file.fileId);
                            }
                          }}
                          data-asset-menu
                          onClick={(e) => {
                            e.stopPropagation();
                            const button = e.currentTarget;
                            if (openMenuFileId === file.fileId) {
                              setOpenMenuFileId(null);
                              setMenuPosition(null);
                            } else {
                              if (typeof window !== 'undefined') {
                                const rect = button.getBoundingClientRect();
                                setMenuPosition({
                                  top: rect.bottom + 4,
                                  right: window.innerWidth - rect.right,
                                });
                              }
                              setOpenMenuFileId(file.fileId);
                            }
                          }}
                          className="absolute top-2 right-2 z-20 p-2 bg-black/60 backdrop-blur-sm border-0 border-white/80 text-white rounded-lg hover:bg-black/80 transition-colors shadow-lg"
                          title="More options"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                      </div>

                      {/* File Info */}
                      <div className="mt-1">
                        <p className="text-xs text-gray-600 truncate" title={file.originalName}>
                          {truncateFileName(file.originalName)}
                        </p>
                        <p className="text-xs text-gray-400">{formatDate(file.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Enlarged Image Viewer - Rendered via Portal */}
      {mounted && enlargedImage && createPortal(
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[10020] p-4"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            // Only close if clicking the backdrop, not the image
            if (e.target === e.currentTarget) {
              setEnlargedImage(null);
            }
          }}
        >
          <div className="relative max-w-full max-h-[90vh] flex items-center justify-center w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button - Top Right */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEnlargedImage(null);
              }}
              className="absolute top-4 right-4 z-20 p-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image Counter - Top Center */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 px-4 py-2 bg-black/50 backdrop-blur-sm text-white text-sm font-medium rounded-lg">
              {enlargedImageIndex + 1} / {getCurrentImageList().length}
            </div>

            {/* Previous Button */}
            {enlargedImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreviousImage();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                aria-label="Previous Image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Next Button */}
            {enlargedImageIndex < getCurrentImageList().length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                aria-label="Next Image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Image Container */}
            <div className="relative w-full max-w-[90vw] h-[90vh] flex items-center justify-center">
              {/* Loading Spinner */}
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <LoadingSpinner size="lg" text="" fullScreen={false} />
                </div>
              )}

              {/* Image */}
              <img
                src={enlargedImage.signedUrl}
                alt={enlargedImage.originalName}
                className={`max-w-full max-h-[90vh] object-contain transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}


